import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, RefreshCw, Download, ExternalLink, X, FileText, Mail, Phone, Building2, Sparkles, RotateCw, Settings, Save, RotateCcw, Zap, Send, Plus, Trash2, Eye, Receipt } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_COLORS = {
  new:        { bg: "bg-cyan-500/15",    border: "border-cyan-400/40",    text: "text-cyan-200" },
  contacted:  { bg: "bg-amber-500/15",   border: "border-amber-400/40",   text: "text-amber-200" },
  quoted:     { bg: "bg-violet-500/15",  border: "border-violet-400/40",  text: "text-violet-200" },
  won:        { bg: "bg-emerald-500/15", border: "border-emerald-400/40", text: "text-emerald-200" },
  lost:       { bg: "bg-zinc-700/40",    border: "border-zinc-600",       text: "text-zinc-400" },
};

function fmtINR(n) { if (!n && n !== 0) return "—"; return "₹" + Math.round(n).toLocaleString("en-IN"); }

function adminHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("admin_token") || ""}` };
}

export default function AdminQuotes() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [openRef, setOpenRef] = useState(null);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refining, setRefining] = useState(false);
  const [refineErr, setRefineErr] = useState("");

  async function load() {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/quotes`, { params: { status: filter }, headers: adminHeaders() });
      setItems(r.data?.items || []);
      setCounts(r.data?.counts || {});
    } catch (e) {
      console.error("[admin-quotes] load", e);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  async function openDetail(ref) {
    setOpenRef(ref);
    setDetail(null);
    setRefineErr("");
    try {
      const r = await axios.get(`${API}/admin/quotes/${ref}`, { headers: adminHeaders() });
      setDetail(r.data?.quote || null);
    } catch (e) {
      console.error("[admin-quotes] detail", e);
    }
  }

  async function refineWithAi(ref) {
    setRefining(true);
    setRefineErr("");
    try {
      const r = await axios.post(`${API}/admin/quotes/${ref}/refine`, {}, { headers: adminHeaders() });
      if (!r.data?.ok) throw new Error("Refinement failed");
      setDetail((d) => d ? { ...d, ai_refinement: r.data.refinement } : d);
    } catch (e) {
      setRefineErr(e?.response?.data?.detail || e?.message || "AI refinement failed");
    } finally { setRefining(false); }
  }

  async function updateStatus(ref, status) {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/quotes/${ref}`, { status }, { headers: adminHeaders() });
      setDetail((d) => d ? { ...d, status } : d);
      setItems((arr) => arr.map((x) => x.reference_id === ref ? { ...x, status } : x));
    } catch (e) {
      alert("Update failed");
    } finally { setSaving(false); }
  }

  async function saveNotes(ref, notes) {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/quotes/${ref}`, { internal_notes: notes }, { headers: adminHeaders() });
      setDetail((d) => d ? { ...d, internal_notes: notes } : d);
    } catch (e) {
      alert("Update failed");
    } finally { setSaving(false); }
  }

  function downloadDoc(ref) {
    window.open(`${API}/admin/quotes/${ref}/doc?token=${localStorage.getItem("admin_token") || ""}`, "_blank");
  }
  function downloadPdf(ref) {
    // Browser cannot inject Authorization headers via window.open; use fetch + blob
    fetch(`${API}/admin/quotes/${ref}/pdf`, { headers: adminHeaders() })
      .then(r => r.blob()).then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `ShansoAI_Quote_${ref}.pdf`; a.click();
        URL.revokeObjectURL(url);
      });
  }

  const total = Object.values(counts).reduce((a,b)=>a+b, 0);

  // ── Pricing rules tuner state ──
  const [showRules, setShowRules] = useState(false);
  const [rulesActive, setRulesActive] = useState(null);
  const [rulesDefaults, setRulesDefaults] = useState(null);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesSaving, setRulesSaving] = useState(false);
  const [rulesErr, setRulesErr] = useState("");

  async function openRules() {
    setShowRules(true);
    setRulesLoading(true);
    setRulesErr("");
    try {
      const r = await axios.get(`${API}/admin/quote-pricing-rules`, { headers: adminHeaders() });
      setRulesActive(r.data?.active || null);
      setRulesDefaults(r.data?.defaults || null);
    } catch (e) {
      setRulesErr(e?.response?.data?.detail || "Failed to load pricing rules");
    } finally { setRulesLoading(false); }
  }

  async function saveRules() {
    if (!rulesActive) return;
    setRulesSaving(true);
    setRulesErr("");
    try {
      const r = await axios.put(`${API}/admin/quote-pricing-rules`, { rules: rulesActive }, { headers: adminHeaders() });
      setRulesActive(r.data?.active || rulesActive);
      alert("Pricing rules saved · all new quotes will use these.");
    } catch (e) {
      setRulesErr(e?.response?.data?.detail || "Save failed");
    } finally { setRulesSaving(false); }
  }

  async function resetRules() {
    if (!confirm("Reset all pricing rules to code defaults? Existing quotes are unaffected.")) return;
    setRulesSaving(true);
    try {
      const r = await axios.post(`${API}/admin/quote-pricing-rules/reset`, {}, { headers: adminHeaders() });
      setRulesActive(r.data?.active || null);
      alert("Reset to defaults.");
    } catch (e) {
      setRulesErr(e?.response?.data?.detail || "Reset failed");
    } finally { setRulesSaving(false); }
  }

  async function runDripTickNow() {
    if (!confirm("Run drip-email tick now? Sends Day-2 + Day-7 nudges to eligible quotes.")) return;
    try {
      const r = await axios.post(`${API}/admin/quote-drip-tick`, {}, { headers: adminHeaders() });
      alert(`Drip tick: ${JSON.stringify(r.data.counts)}`);
    } catch (e) {
      alert(e?.response?.data?.detail || "Drip tick failed");
    }
  }

  function updateRuleField(group, key, field, value) {
    setRulesActive((R) => {
      if (!R) return R;
      const next = JSON.parse(JSON.stringify(R));
      if (next[group] && next[group][key]) {
        next[group][key][field] = value;
      }
      return next;
    });
  }
  function updateScalar(key, value) {
    setRulesActive((R) => R ? { ...R, [key]: value } : R);
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 text-white max-w-6xl mx-auto" data-testid="admin-quotes-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">Website Quote Requests</h2>
          <p className="text-zinc-400 text-sm mt-1">{total} total · {counts.new || 0} new · {counts.won || 0} won</p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/admin/subscriptions" className="inline-flex items-center gap-2 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm hover:border-emerald-400 hover:text-emerald-200" data-testid="open-subscriptions-link">
            <RotateCw size={14}/> Subscriptions
          </a>
          <button onClick={runDripTickNow} className="inline-flex items-center gap-2 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm hover:border-amber-400 hover:text-amber-200" data-testid="run-drip-tick-btn" title="Manually trigger Day-2 + Day-7 drip email pass">
            <Zap size={14}/> Drip tick
          </button>
          <button onClick={openRules} className="inline-flex items-center gap-2 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm hover:border-violet-400 hover:text-violet-200" data-testid="open-pricing-rules-btn">
            <Settings size={14}/> Pricing rules
          </button>
          <button onClick={load} className="inline-flex items-center gap-2 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm hover:border-zinc-500" data-testid="refresh-quotes">
            <RefreshCw size={14}/> Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {["all", "new", "contacted", "quoted", "won", "lost"].map((s) => (
          <button key={s} onClick={()=>setFilter(s)} data-testid={`filter-${s}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${filter===s ? "bg-cyan-500/15 border-cyan-400 text-cyan-200" : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
            {s.charAt(0).toUpperCase() + s.slice(1)} {s !== "all" && `(${counts[s] || 0})`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-500"><Loader2 size={20} className="animate-spin"/></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-zinc-500" data-testid="quotes-empty">
          No quote requests {filter !== "all" ? `in status "${filter}"` : "yet"}.
        </div>
      ) : (
        <div className="space-y-2" data-testid="quotes-list">
          {items.map((q) => {
            const sc = STATUS_COLORS[q.status] || STATUS_COLORS.new;
            return (
              <button key={q.reference_id} onClick={()=>openDetail(q.reference_id)} data-testid={`quote-row-${q.reference_id}`}
                className="w-full text-left bg-zinc-900/60 border border-zinc-800 rounded-xl px-4 py-3 hover:border-zinc-600 transition-colors">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <code className="text-cyan-300 font-mono text-xs">{q.reference_id}</code>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${sc.bg} ${sc.border} ${sc.text}`}>{q.status}</span>
                      {q.advance_paid && <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-500/15 border border-emerald-400/40 text-emerald-200">Advance paid</span>}
                    </div>
                    <p className="text-white text-sm font-semibold">{q.customer?.full_name} <span className="text-zinc-500 font-normal">· {q.customer?.email}</span></p>
                    <p className="text-zinc-500 text-xs mt-0.5">{q.state?.project_type} · {q.state?.pages} pages · {q.state?.timeline}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-cyan-300 font-bold">{fmtINR(q.pricing?.total)}</p>
                    <p className="text-zinc-500 text-[11px]">advance {fmtINR(q.pricing?.advance_amount)}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {openRef && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={()=>setOpenRef(null)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl max-w-2xl w-full p-6 my-8" onClick={(e)=>e.stopPropagation()} data-testid="quote-detail-modal">
            {!detail ? (
              <div className="flex items-center justify-center py-12"><Loader2 size={20} className="animate-spin text-zinc-500"/></div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div>
                    <code className="text-cyan-300 font-mono text-sm">{detail.reference_id}</code>
                    <h3 className="text-xl font-bold text-white mt-1">{detail.customer.full_name}</h3>
                    <p className="text-zinc-400 text-sm">{detail.customer.company || "—"}</p>
                  </div>
                  <button onClick={()=>setOpenRef(null)} className="text-zinc-500 hover:text-white" data-testid="close-detail"><X size={20}/></button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs mb-5">
                  <div className="flex items-center gap-2 text-zinc-300"><Mail size={12} className="text-zinc-500"/> {detail.customer.email}</div>
                  <div className="flex items-center gap-2 text-zinc-300"><Phone size={12} className="text-zinc-500"/> {detail.customer.phone}</div>
                  <div className="flex items-center gap-2 text-zinc-300"><Building2 size={12} className="text-zinc-500"/> {detail.customer.best_time_to_reach || "Anytime"}</div>
                </div>

                <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4 mb-4">
                  <p className="text-xs text-zinc-500 uppercase mb-2">Business overview</p>
                  <p className="text-sm text-zinc-200 whitespace-pre-wrap">{detail.state.business_overview || "—"}</p>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs mb-4">
                  <Cell label="Type" value={detail.state.project_type}/>
                  <Cell label="Pages" value={detail.state.pages}/>
                  <Cell label="Languages" value={detail.state.languages}/>
                  <Cell label="Timeline" value={detail.state.timeline}/>
                  <Cell label="SEO" value={detail.state.seo_package}/>
                  <Cell label="SKU tier" value={detail.state.sku_tier || "—"}/>
                </div>
                <Cell label="Features" value={(detail.state.features||[]).join(", ") || "—"} />
                <Cell label="Commerce" value={(detail.state.commerce||[]).join(", ") || "—"} />
                <Cell label="Gateways" value={(detail.state.payment_gateways||[]).join(", ") || "—"} />
                <Cell label="Branding" value={(detail.state.branding||[]).join(", ") || "—"} />
                {detail.notes_from_customer && <Cell label="Customer notes" value={detail.notes_from_customer}/>}

                <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4 my-4">
                  <p className="text-xs text-zinc-500 uppercase mb-2">Pricing breakdown</p>
                  <div className="text-cyan-300 text-2xl font-bold mb-2">{fmtINR(detail.pricing.total)}</div>
                  <ul className="text-xs space-y-1 text-zinc-300">
                    {detail.pricing.line_items.map((li, i) => (
                      <li key={i} className="flex justify-between border-b border-zinc-800 pb-1"><span>{li.label}</span><span className="text-zinc-400">{fmtINR(li.amount)}</span></li>
                    ))}
                  </ul>
                </div>

                <QuotationEditor detail={detail} setDetail={setDetail} setItems={setItems} />

                <div className="flex flex-wrap gap-2 mb-4">
                  {detail.uploaded_doc && (
                    <button onClick={()=>downloadDoc(detail.reference_id)} className="inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-xs hover:border-cyan-400" data-testid="download-doc-btn">
                      <FileText size={12}/> {detail.uploaded_doc.filename}
                    </button>
                  )}
                  <button onClick={()=>downloadPdf(detail.reference_id)} className="inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-xs hover:border-cyan-400" data-testid="download-pdf-btn">
                    <Download size={12}/> Quote PDF
                  </button>
                  <a href={`mailto:${detail.customer.email}?subject=Re: ${detail.reference_id}`} className="inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-xs hover:border-cyan-400">
                    <Mail size={12}/> Email
                  </a>
                  <button onClick={()=>refineWithAi(detail.reference_id)} disabled={refining} data-testid="refine-with-ai-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold disabled:opacity-60"
                    style={{ background: "linear-gradient(135deg, #7000FF, #C026D3)", color: "#fff" }}>
                    {refining ? <><Loader2 size={12} className="animate-spin"/> Analyzing…</> : detail.ai_refinement ? <><RotateCw size={12}/> Re-refine</> : <><Sparkles size={12}/> Refine with AI</>}
                  </button>
                </div>

                {refineErr && (
                  <div className="mb-4 px-3 py-2 rounded-lg border border-red-500/40 bg-red-500/10 text-red-200 text-xs" data-testid="refine-error">
                    ⚠ {refineErr}
                  </div>
                )}

                {detail.ai_refinement && (
                  <div className="mb-4 rounded-lg border border-violet-500/30 p-4" style={{ background: "linear-gradient(135deg, rgba(112,0,255,0.08), rgba(192,38,211,0.04), rgba(24,24,27,0.6))" }} data-testid="ai-refinement-panel">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2">
                        <Sparkles size={14} className="text-violet-300"/>
                        <p className="text-violet-200 text-xs font-bold uppercase tracking-wider">AI strategist analysis</p>
                      </div>
                      <span className="text-[10px] text-zinc-500" data-testid="ai-refinement-meta">
                        {detail.ai_refinement.model} · {detail.ai_refinement.doc_extracted_chars || 0} chars from brief
                      </span>
                    </div>
                    <div className="prose prose-invert prose-sm max-w-none text-zinc-100 leading-relaxed [&_h2]:text-violet-200 [&_h2]:text-sm [&_h2]:font-bold [&_h2]:mt-4 [&_h2]:mb-2 [&_ul]:mt-1 [&_ul]:mb-2 [&_li]:my-0.5 [&_p]:my-1.5 [&_strong]:text-white whitespace-pre-wrap text-sm">
                      {detail.ai_refinement.markdown}
                    </div>
                    <button onClick={()=>{ navigator.clipboard.writeText(detail.ai_refinement.markdown); }} data-testid="copy-refinement-btn"
                      className="mt-3 text-[11px] text-zinc-400 hover:text-violet-300 underline">
                      Copy analysis to clipboard
                    </button>
                  </div>
                )}

                <div className="border-t border-zinc-800 pt-4">
                  <p className="text-xs text-zinc-500 uppercase mb-2">Update status</p>
                  <div className="flex flex-wrap gap-2">
                    {["new","contacted","quoted","won","lost"].map((s)=>{
                      const sc = STATUS_COLORS[s];
                      const active = detail.status === s;
                      return (
                        <button key={s} disabled={saving} onClick={()=>updateStatus(detail.reference_id, s)} data-testid={`set-status-${s}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${active ? `${sc.bg} ${sc.border} ${sc.text}` : "bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}>
                          {s}
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-4">
                    <p className="text-xs text-zinc-500 uppercase mb-2">Internal notes</p>
                    <textarea defaultValue={detail.internal_notes || ""} onBlur={(e)=>saveNotes(detail.reference_id, e.target.value)} rows={3}
                      data-testid="internal-notes-input"
                      placeholder="Private notes (auto-saves on blur)…"
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white focus:border-cyan-400 focus:outline-none"/>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {showRules && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={()=>setShowRules(false)} data-testid="pricing-rules-modal">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl max-w-4xl w-full p-6 my-8" onClick={(e)=>e.stopPropagation()}>
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="text-xl font-bold text-white flex items-center gap-2"><Settings size={18} className="text-violet-300"/> Pricing Rules</h3>
                <p className="text-zinc-400 text-xs mt-1">Tune base prices, add-ons, timeline multipliers, advance %, and validity. Saves apply to all new quotes immediately. Existing quotes keep their snapshot.</p>
              </div>
              <button onClick={()=>setShowRules(false)} className="text-zinc-500 hover:text-white" data-testid="close-rules-modal"><X size={20}/></button>
            </div>

            {rulesErr && <div className="mb-3 px-3 py-2 rounded-lg border border-red-500/40 bg-red-500/10 text-red-200 text-xs">⚠ {rulesErr}</div>}

            {rulesLoading || !rulesActive ? (
              <div className="flex items-center justify-center py-16"><Loader2 size={20} className="animate-spin text-zinc-500"/></div>
            ) : (
              <>
                {/* Global settings */}
                <RulesSection title="Global settings">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <NumField label="Advance %" value={rulesActive.advance_pct} step={0.01} suffix="(0–1)"
                      onChange={(v)=>updateScalar("advance_pct", v)} testid="rule-advance-pct"/>
                    <NumField label="Uncertainty band" value={rulesActive.uncertainty_band} step={0.01} suffix="(0–1, ±range)"
                      onChange={(v)=>updateScalar("uncertainty_band", v)} testid="rule-uncertainty-band"/>
                    <NumField label="Quote validity days" value={rulesActive.quote_validity_days} step={1}
                      onChange={(v)=>updateScalar("quote_validity_days", v)} testid="rule-validity-days"/>
                  </div>
                </RulesSection>

                <RulesSection title="Project type — base prices (₹)">
                  <RuleGrid group={rulesActive.project_type} field="base" onChange={(k,v)=>updateRuleField("project_type", k, "base", v)} testidPrefix="rule-project-type"/>
                </RulesSection>

                <RulesSection title="Pages — add (₹)">
                  <RuleGrid group={rulesActive.pages} field="add" onChange={(k,v)=>updateRuleField("pages", k, "add", v)} testidPrefix="rule-pages"/>
                </RulesSection>

                <RulesSection title="Timeline — multipliers (1.0 = standard)">
                  <RuleGrid group={rulesActive.timeline} field="mult" step={0.01} onChange={(k,v)=>updateRuleField("timeline", k, "mult", v)} testidPrefix="rule-timeline"/>
                </RulesSection>

                <RulesSection title="Languages">
                  <RuleGrid group={rulesActive.languages} field="add" onChange={(k,v)=>updateRuleField("languages", k, "add", v)} testidPrefix="rule-languages"/>
                </RulesSection>

                <RulesSection title="Features — add (₹)">
                  <RuleGrid group={rulesActive.features} field="add" onChange={(k,v)=>updateRuleField("features", k, "add", v)} testidPrefix="rule-features" cols={2}/>
                </RulesSection>

                <RulesSection title="Commerce features — add (₹)">
                  <RuleGrid group={rulesActive.commerce} field="add" onChange={(k,v)=>updateRuleField("commerce", k, "add", v)} testidPrefix="rule-commerce" cols={2}/>
                </RulesSection>

                <RulesSection title="SKU tier — add (₹)">
                  <RuleGrid group={rulesActive.sku_tier} field="add" onChange={(k,v)=>updateRuleField("sku_tier", k, "add", v)} testidPrefix="rule-sku"/>
                </RulesSection>

                <RulesSection title="Payment gateways — add (₹)">
                  <RuleGrid group={rulesActive.payment_gateways} field="add" onChange={(k,v)=>updateRuleField("payment_gateways", k, "add", v)} testidPrefix="rule-gateway"/>
                </RulesSection>

                <RulesSection title="SEO package — add (₹)">
                  <RuleGrid group={rulesActive.seo_package} field="add" onChange={(k,v)=>updateRuleField("seo_package", k, "add", v)} testidPrefix="rule-seo"/>
                </RulesSection>

                <RulesSection title="Marketing add-ons — add (₹)">
                  <RuleGrid group={rulesActive.marketing_addons} field="add" onChange={(k,v)=>updateRuleField("marketing_addons", k, "add", v)} testidPrefix="rule-marketing"/>
                </RulesSection>

                <RulesSection title="Branding — add (₹)">
                  <RuleGrid group={rulesActive.branding} field="add" onChange={(k,v)=>updateRuleField("branding", k, "add", v)} testidPrefix="rule-branding"/>
                </RulesSection>

                <div className="sticky bottom-0 -mx-6 -mb-6 mt-6 px-6 py-4 bg-zinc-900/95 backdrop-blur border-t border-zinc-800 flex flex-wrap items-center gap-3 justify-between">
                  <button onClick={resetRules} disabled={rulesSaving} className="inline-flex items-center gap-2 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-xs hover:border-red-400 hover:text-red-200 disabled:opacity-50" data-testid="reset-rules-btn">
                    <RotateCcw size={12}/> Reset to defaults
                  </button>
                  <div className="flex items-center gap-2">
                    <button onClick={()=>setShowRules(false)} className="px-3 py-2 text-zinc-400 text-xs hover:text-white">Cancel</button>
                    <button onClick={saveRules} disabled={rulesSaving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold disabled:opacity-50" style={{ background: "linear-gradient(135deg, #7000FF, #C026D3)", color: "#fff" }} data-testid="save-rules-btn">
                      {rulesSaving ? <><Loader2 size={14} className="animate-spin"/> Saving…</> : <><Save size={14}/> Save & apply</>}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function QuotationEditor({ detail, setDetail, setItems }) {
  const ref = detail.reference_id;
  const [q, setQ] = useState(detail.quotation || null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => { setQ(detail.quotation || null); setErr(""); }, [ref]); // eslint-disable-line

  const subtotal = (q?.line_items || []).reduce((a, li) => a + (Number(li.amount) || 0), 0);
  const total = Math.max(subtotal - (Number(q?.discount_amount) || 0), 0);

  function setItem(i, field, value) {
    setQ((cur) => ({ ...cur, line_items: cur.line_items.map((li, idx) => idx === i ? { ...li, [field]: value } : li) }));
  }
  function addItem() {
    setQ((cur) => ({ ...cur, line_items: [...cur.line_items, { label: "", amount: 0 }] }));
  }
  function removeItem(i) {
    setQ((cur) => ({ ...cur, line_items: cur.line_items.filter((_, idx) => idx !== i) }));
  }

  async function generate() {
    setBusy("generate"); setErr("");
    try {
      const r = await axios.post(`${API}/admin/quotes/${ref}/quotation/generate`, {}, { headers: adminHeaders() });
      setQ(r.data.quotation);
      setDetail((d) => d ? { ...d, quotation: r.data.quotation } : d);
    } catch (e) { setErr(e?.response?.data?.detail || "Generate failed"); }
    finally { setBusy(""); }
  }

  async function persist() {
    const payload = {
      line_items: q.line_items.map((li) => ({ label: (li.label || "").trim(), amount: Math.round(Number(li.amount) || 0) })),
      discount_label: q.discount_label || "",
      discount_amount: Math.round(Number(q.discount_amount) || 0),
      admin_note: q.admin_note || "",
      valid_days: Math.round(Number(q.valid_days) || 15),
    };
    const r = await axios.put(`${API}/admin/quotes/${ref}/quotation`, payload, { headers: adminHeaders() });
    setQ(r.data.quotation);
    setDetail((d) => d ? { ...d, quotation: r.data.quotation } : d);
  }

  async function saveDraft() {
    setBusy("save"); setErr("");
    try { await persist(); } catch (e) { setErr(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(""); }
  }

  async function previewPdf() {
    setBusy("preview"); setErr("");
    try {
      await persist();
      const r = await fetch(`${API}/admin/quotes/${ref}/quotation/pdf`, { headers: adminHeaders() });
      if (!r.ok) throw new Error("PDF generation failed");
      const url = URL.createObjectURL(await r.blob());
      window.open(url, "_blank");
    } catch (e) { setErr(e?.response?.data?.detail || e.message || "Preview failed"); }
    finally { setBusy(""); }
  }

  async function sendToCustomer() {
    if (!window.confirm(`Send quotation of ${fmtINR(total)} to ${detail.customer.email}?`)) return;
    setBusy("send"); setErr("");
    try {
      await persist();
      const r = await axios.post(`${API}/admin/quotes/${ref}/quotation/send`, {}, { headers: adminHeaders() });
      setQ(r.data.quotation);
      setDetail((d) => d ? { ...d, quotation: r.data.quotation, status: r.data.status || d.status } : d);
      if (r.data.status && setItems) setItems((arr) => arr.map((x) => x.reference_id === ref ? { ...x, status: r.data.status } : x));
    } catch (e) { setErr(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(""); }
  }

  return (
    <div className="bg-zinc-950 border border-cyan-500/25 rounded-lg p-4 mb-4" data-testid="quotation-panel">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Receipt size={14} className="text-cyan-300"/>
          <p className="text-cyan-200 text-xs font-bold uppercase tracking-wider">Customer quotation</p>
        </div>
        {q?.sent_at && (
          <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-500/15 border border-emerald-400/40 text-emerald-200" data-testid="quotation-sent-badge">
            Sent v{q.version} · {new Date(q.sent_at).toLocaleDateString("en-IN")}
          </span>
        )}
      </div>

      {err && <div className="mb-3 px-3 py-2 rounded-lg border border-red-500/40 bg-red-500/10 text-red-200 text-xs" data-testid="quotation-error">⚠ {err}</div>}

      {!q ? (
        <div className="text-center py-4">
          <p className="text-zinc-400 text-xs mb-3">No quotation yet. Generate an engine-assisted estimate from this requirement, then verify &amp; adjust before sending.</p>
          <button onClick={generate} disabled={busy === "generate"} data-testid="quotation-generate-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold disabled:opacity-60"
            style={{ background: "linear-gradient(135deg, #0891b2, #22D3EE)", color: "#000" }}>
            {busy === "generate" ? <><Loader2 size={14} className="animate-spin"/> Generating…</> : <><Sparkles size={14}/> Generate engine estimate</>}
          </button>
        </div>
      ) : (
        <>
          <div className="space-y-1.5 mb-3">
            {q.line_items.map((li, i) => (
              <div key={i} className="flex items-center gap-2">
                <input value={li.label} onChange={(e)=>setItem(i, "label", e.target.value)} placeholder="Description"
                  data-testid={`quotation-item-label-${i}`}
                  className="flex-1 px-2.5 py-1.5 bg-zinc-800 border border-zinc-700 rounded-md text-xs text-white focus:border-cyan-400 focus:outline-none"/>
                <input type="number" value={li.amount} onChange={(e)=>setItem(i, "amount", e.target.value)}
                  data-testid={`quotation-item-amount-${i}`}
                  className="w-28 px-2.5 py-1.5 bg-zinc-800 border border-zinc-700 rounded-md text-xs text-white font-mono text-right focus:border-cyan-400 focus:outline-none"/>
                <button onClick={()=>removeItem(i)} data-testid={`quotation-remove-item-${i}`} className="text-zinc-600 hover:text-red-400"><Trash2 size={13}/></button>
              </div>
            ))}
            <button onClick={addItem} data-testid="quotation-add-item-btn" className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-cyan-300 mt-1">
              <Plus size={11}/> Add line item
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
            <label className="block">
              <span className="block text-[10px] text-zinc-500 uppercase mb-1">Discount label</span>
              <input value={q.discount_label || ""} onChange={(e)=>setQ({...q, discount_label: e.target.value})} placeholder="e.g. Launch offer"
                data-testid="quotation-discount-label"
                className="w-full px-2.5 py-1.5 bg-zinc-800 border border-zinc-700 rounded-md text-xs text-white focus:border-cyan-400 focus:outline-none"/>
            </label>
            <label className="block">
              <span className="block text-[10px] text-zinc-500 uppercase mb-1">Discount (₹)</span>
              <input type="number" value={q.discount_amount || 0} onChange={(e)=>setQ({...q, discount_amount: e.target.value})}
                data-testid="quotation-discount-amount"
                className="w-full px-2.5 py-1.5 bg-zinc-800 border border-zinc-700 rounded-md text-xs text-white font-mono focus:border-cyan-400 focus:outline-none"/>
            </label>
            <label className="block">
              <span className="block text-[10px] text-zinc-500 uppercase mb-1">Valid for (days)</span>
              <input type="number" value={q.valid_days || 15} onChange={(e)=>setQ({...q, valid_days: e.target.value})}
                data-testid="quotation-valid-days"
                className="w-full px-2.5 py-1.5 bg-zinc-800 border border-zinc-700 rounded-md text-xs text-white font-mono focus:border-cyan-400 focus:outline-none"/>
            </label>
          </div>

          <textarea value={q.admin_note || ""} onChange={(e)=>setQ({...q, admin_note: e.target.value})} rows={2}
            data-testid="quotation-note-input"
            placeholder="Note to customer (appears in the email + PDF)…"
            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-xs text-white focus:border-cyan-400 focus:outline-none mb-3"/>

          <div className="flex items-center justify-between flex-wrap gap-3 border-t border-zinc-800 pt-3">
            <div>
              <p className="text-[10px] text-zinc-500 uppercase">Total {Number(q.discount_amount) > 0 && <span className="text-emerald-400">(after discount)</span>}</p>
              <p className="text-cyan-300 text-xl font-bold" data-testid="quotation-total">{fmtINR(total)}</p>
              {q.engine_total != null && <p className="text-[10px] text-zinc-600">engine suggested {fmtINR(q.engine_total)}</p>}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={generate} disabled={!!busy} title="Regenerate from engine (overwrites line items)" data-testid="quotation-regenerate-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-xs hover:border-cyan-400 disabled:opacity-50">
                <RotateCw size={12}/> Regenerate
              </button>
              <button onClick={saveDraft} disabled={!!busy} data-testid="quotation-save-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-xs hover:border-cyan-400 disabled:opacity-50">
                {busy === "save" ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Save draft
              </button>
              <button onClick={previewPdf} disabled={!!busy} data-testid="quotation-preview-pdf-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-xs hover:border-cyan-400 disabled:opacity-50">
                {busy === "preview" ? <Loader2 size={12} className="animate-spin"/> : <Eye size={12}/>} Preview PDF
              </button>
              <button onClick={sendToCustomer} disabled={!!busy} data-testid="quotation-send-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold disabled:opacity-60"
                style={{ background: "linear-gradient(135deg, #0891b2, #22D3EE)", color: "#000" }}>
                {busy === "send" ? <><Loader2 size={12} className="animate-spin"/> Sending…</> : <><Send size={12}/> {q.sent_at ? "Re-send to customer" : "Send to customer"}</>}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function RulesSection({ title, children }) {
  return (
    <div className="mb-5">
      <p className="text-xs font-bold uppercase tracking-wider text-violet-300 mb-2">{title}</p>
      {children}
    </div>
  );
}

function RuleGrid({ group, field, onChange, testidPrefix, step = 1, cols = 3 }) {
  if (!group) return null;
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-${cols} gap-2`}>
      {Object.entries(group).map(([k, def]) => (
        <NumField key={k} label={def.label || k} value={def[field] ?? 0} step={step}
          onChange={(v)=>onChange(k, v)} testid={`${testidPrefix}-${k}`}/>
      ))}
    </div>
  );
}

function NumField({ label, value, step = 1, suffix, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-[11px] text-zinc-400 mb-1">{label} {suffix && <span className="text-zinc-600">{suffix}</span>}</span>
      <input type="number" step={step} value={value} data-testid={testid}
        onChange={(e)=>onChange(step >= 1 ? Math.round(Number(e.target.value)||0) : Number(e.target.value)||0)}
        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-white text-sm font-mono focus:border-violet-400 focus:outline-none"/>
    </label>
  );
}

function Cell({ label, value }) {
  return (
    <div className="mb-1.5">
      <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className="text-sm text-zinc-200">{value || "—"}</p>
    </div>
  );
}
